import { useState, useRef, useEffect, useCallback } from "react";
import jsQR from "jsqr";

export interface QRScanResult {
  data: string;
  timestamp: Date;
  type: string;
}

export interface QRScannerState {
  isScanning: boolean;
  hasPermission: boolean | null;
  error: string | null;
  lastScannedCode: QRScanResult | null;
  streamActive: boolean;
  isTorchSupported: boolean;
  isTorchOn: boolean;
}

interface CanvasImageData {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

export function useQRScanner(
  onScan?: (result: QRScanResult) => void,
  autoStart = true
) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scanningRef = useRef(false);
  const requestAnimationFrameRef = useRef<number | null>(null);
  const lastScannedRef = useRef<string | null>(null);

  const [state, setState] = useState<QRScannerState>({
    isScanning: autoStart,
    hasPermission: null,
    error: null,
    lastScannedCode: null,
    streamActive: false,
    isTorchSupported: false,
    isTorchOn: false,
  });

  // Scan frame from video
  const scanFrame = useCallback(() => {
    if (
      !videoRef.current ||
      !canvasRef.current ||
      !scanningRef.current ||
      videoRef.current.readyState !== videoRef.current.HAVE_ENOUGH_DATA
    ) {
      if (scanningRef.current) {
        requestAnimationFrameRef.current = requestAnimationFrame(scanFrame);
      }
      return;
    }

    const canvas = canvasRef.current;
    const video = videoRef.current;

    // Set canvas dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      if (scanningRef.current) {
        requestAnimationFrameRef.current = requestAnimationFrame(scanFrame);
      }
      return;
    }

    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Get image data and scan for QR code
    try {
      const imageData = ctx.getImageData(
        0,
        0,
        canvas.width,
        canvas.height
      ) as CanvasImageData;
      const qrCode = jsQR(imageData.data, imageData.width, imageData.height);

      if (qrCode && qrCode.data !== lastScannedRef.current) {
        lastScannedRef.current = qrCode.data;

        const result: QRScanResult = {
          data: qrCode.data,
          timestamp: new Date(),
          type: "qr_code",
        };

        setState((prev) => ({
          ...prev,
          lastScannedCode: result,
          error: null,
        }));

        if (onScan) {
          onScan(result);
        }

        // Reset after 2 seconds to allow re-scanning
        setTimeout(() => {
          lastScannedRef.current = null;
        }, 2000);
      }
    } catch (err: any) {
      console.error("QR scan error:", err);
    }

    // Continue scanning
    if (scanningRef.current) {
      requestAnimationFrameRef.current = requestAnimationFrame(scanFrame);
    }
  }, [onScan]);

  // Start scanning
  const startScanning = useCallback(async () => {
    try {
      if (streamRef.current) {
        // Stream already active
        scanningRef.current = true;
        setState((prev) => ({ ...prev, isScanning: true, error: null }));
        scanFrame();
        return;
      }

      // Request camera permission
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // Check torch support
      let isTorchSupported = false;
      try {
        const track = stream.getVideoTracks()[0];
        if (track) {
          const capabilities = (track as any).getCapabilities?.();
          isTorchSupported = capabilities?.torch ? true : false;
        }
      } catch (e) {
        // Torch not supported
      }

      scanningRef.current = true;
      setState((prev) => ({
        ...prev,
        isScanning: true,
        hasPermission: true,
        streamActive: true,
        error: null,
        isTorchSupported,
      }));

      // Wait for video to be ready before starting scan
      if (videoRef.current) {
        videoRef.current.onloadedmetadata = () => {
          scanFrame();
        };
      }
    } catch (err: any) {
      const errorMessage =
        err.name === "NotAllowedError"
          ? "Camera permission denied. Please allow camera access."
          : err.name === "NotFoundError"
            ? "No camera device found."
            : err.message || "Failed to access camera";

      setState((prev) => ({
        ...prev,
        isScanning: false,
        hasPermission: false,
        error: errorMessage,
        streamActive: false,
      }));

      console.error("Camera access error:", err);
    }
  }, [scanFrame]);

  // Stop scanning
  const stopScanning = useCallback(() => {
    scanningRef.current = false;

    if (requestAnimationFrameRef.current) {
      cancelAnimationFrame(requestAnimationFrameRef.current);
      requestAnimationFrameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setState((prev) => ({
      ...prev,
      isScanning: false,
      streamActive: false,
      isTorchOn: false,
    }));
  }, []);

  // Toggle torch
  const toggleTorch = useCallback(async () => {
    if (!state.isTorchSupported || !streamRef.current) return;

    try {
      const track = streamRef.current.getVideoTracks()[0];
      if (track) {
        await (track as any).applyConstraints({
          advanced: [{ torch: !state.isTorchOn }],
        });
        setState((prev) => ({ ...prev, isTorchOn: !prev.isTorchOn }));
      }
    } catch (error) {
      console.error("Failed to toggle torch:", error);
    }
  }, [state.isTorchSupported, state.isTorchOn]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanning();
    };
  }, [stopScanning]);

  // Auto-start if enabled
  useEffect(() => {
    if (autoStart && !state.isScanning) {
      startScanning();
    }
  }, [autoStart, state.isScanning, startScanning]);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const clearLastScanned = useCallback(() => {
    setState((prev) => ({ ...prev, lastScannedCode: null }));
  }, []);

  return {
    ...state,
    videoRef,
    canvasRef,
    startScanning,
    stopScanning,
    clearError,
    clearLastScanned,
    toggleTorch,
  };
}
